import { nonnull } from "@tento-core/assert";
import { AVLTree } from "@tento-core/avl-tree";
import { NumRangeLike } from "@tento-core/range";
import { ScrollWindow } from "./scroll-window";

export class ScrollRangeManager {
  private widthIndex: AVLTree<
    AVLTree.Node.Many<string, ScrollRangeManager.Node<any>>
  > = new AVLTree();

  private fixedElements: Map<
    any,
    { index: number; onUpdate: ($e: any, offset: number) => void }
  > = new Map();

  constructor(private readonly window: ScrollWindow) {}

  /** Unregister a fixed child. Every dismissal path MUST call this — the map
   *  has no other pruning, so a missed delete is iterated by every
   *  `updateOffset` for the rest of the session. */
  removeFixedChild<K>($e: K): boolean {
    return this.fixedElements.delete($e);
  }

  updateOffset(index: number, offset: number) {
    for (const [$e, v] of this.fixedElements.entries()) {
      if (v.index !== index) continue;
      v.onUpdate($e, offset);
    }
  }

  updateFixedChildIndex<K>($e: K, newIndex: number): Option<number> {
    const entry = this.fixedElements.get($e);
    if (!entry) return null;

    // Extend window if needed (same logic as setFixedChildIndex)
    if (newIndex > this.window.maxIndex) {
      this.window.extendToIdxRange({
        start: this.window.maxIndex,
        end: newIndex,
      });
    }
    if (newIndex < this.window.minIndex) {
      this.window.extendToIdxRange({
        start: newIndex,
        end: this.window.minIndex,
      });
    }

    entry.index = newIndex;
    // Return the new offset - caller is responsible for updating position
    // (we don't call onUpdate here to avoid recursion since this is called from xPosition)
    return this.window.getOffset(newIndex);
  }

  setFixedChildIndex<K>(
    $e: K,
    index: number,
    onUpdate: ($e: K, offset: number) => void,
    triggerImmediately: boolean = true,
  ): number {
    if (index > this.window.maxIndex) {
      this.window.extendToIdxRange({
        start: this.window.maxIndex,
        end: index,
      });
    }
    if (index < this.window.minIndex) {
      this.window.extendToIdxRange({
        start: index,
        end: this.window.minIndex,
      });
    }
    const offset = nonnull(this.window.getOffset(index));
    triggerImmediately && onUpdate($e, offset);
    this.fixedElements.set($e, {
      index,
      onUpdate,
    });
    return offset;
  }

  setFixedChildWidth<K>(
    $k: K,
    id: string,
    range: NumRangeLike,
    onUpdate: ($e: K, width: number) => void,
  ) {
    this.window.extendToIdxRange({
      start: this.window.maxIndex,
      end: range.end + 1,
    });
    onUpdate(
      $k,
      nonnull(this.window.getOffset(range.end + 1)) -
        nonnull(this.window.getOffset(range.start)),
    );
    this.widthIndex.insert(
      new AVLTree.Node.Many([
        {
          id,
          range,
          $k,
          updater: onUpdate,
        },
      ]),
    );
  }
}

export namespace ScrollRangeManager {
  export type Updater<K> = ($e: K, width: number) => void;
  export type Node<K> = {
    id: string;
    range: NumRangeLike;
    $k: K;
    updater: Updater<K>;
  };
}
